import { createPortal } from 'react-dom';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface Props {
  anchorRef: React.RefObject<HTMLElement>;
  children: React.ReactNode;
}

export const PortalTooltip: React.FC<Props> = ({ anchorRef, children }) => {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const [ready, setReady] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR guard

    const updatePosition = () => {
      const anchor = anchorRef.current;
      const tooltip = tooltipRef.current;
      if (!anchor || !tooltip) return;

      tooltip.style.visibility = 'hidden';
      tooltip.style.left = '0';
      tooltip.style.top = '0';

      const anchorRect = anchor.getBoundingClientRect();
      const tooltipRect = tooltip.getBoundingClientRect();

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

      let left =
        anchorRect.left +
        anchorRect.width / 2 -
        tooltipRect.width / 2 +
        scrollLeft;

      const minLeft = scrollLeft + 8;
      const maxLeft = scrollLeft + window.innerWidth - tooltipRect.width - 8;
      left = Math.max(minLeft, Math.min(maxLeft, left));

      const top = anchorRect.top - tooltipRect.height - 8 + scrollTop;

      setStyle({
        position: 'absolute',
        left,
        top,
        zIndex: 9999,
      });

      tooltip.style.visibility = 'visible';
      setReady(true);
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [anchorRef]);

  return createPortal(
    <TooltipContainer ref={tooltipRef as React.Ref<any> } style={style} data-ready={ready}>
      {children}
    </TooltipContainer>,
    document.body,
  );
};

const TooltipContainer = styled.div`
  background-color: #333;
  color: #fff;
  text-align: center;
  padding: 6px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  position: absolute;
  visibility: hidden;
  &[data-ready='true'] {
    visibility: visible;
  }
`;
